import InfoLayout from "../components/InfoLayout";
import usePageMeta from "../hooks/usePageMeta";
import { addOns, chargeNoteByKey, completeAssuranceByBrand, packagesFor, v5cBundleNoteFor } from "../config/pricing";
import { CHEAPEST_HPI_PATH, hasCheapestHpiPage } from "../content/cheapestHpiCheck";
import { serviceBossPricingMeta } from "../../lib/serviceBossSeo.js";

// Buyers in this category compare on price before they land, and roundup
// articles cite whatever price they can read. Pricing used to live only at
// /#pricing, an anchor on a client-rendered page, so no crawler and no AI
// answer engine could see a number anywhere on the site. This is the same
// content at a real URL, mirrored by scripts/prerender-pages.mjs so it is in
// the initial HTML response too.
export default function PricingPage({ brand }) {
  const packages = packagesFor(brand.key);
  const [risk, complete] = packages;

  usePageMeta({
    title: brand.key === "serviceboss" ? serviceBossPricingMeta.title : brand.key === "servicelad" ? "Full HPI Check £7.95, with Service History £14.95 | ServiceLad" : `Pricing | ${brand.name}`,
    description: brand.key === "serviceboss" ? serviceBossPricingMeta.description : `${risk.name} £${risk.plate}, ${complete.name} £${complete.plate}. One-off payments, no subscription. See exactly what each ${brand.name} check includes.`,
  });

  return (
    <InfoLayout
      brand={brand}
      eyebrow="Simple, honest pricing"
      title="Choose the check that fits."
      intro={`A ${risk.name} costs £${risk.plate} and a ${complete.name} costs £${complete.plate}. Both are one-off payments for a single registration. No subscription, no renewal, no membership.`}
    >
      <section className="content">
        <div className="wrap prose">
          <h2>What each check costs and covers</h2>
          <div className="jobsheet-grid">
            {packages.map((plan) => (
              <article className={`sheet${plan.featured ? " sheet-featured" : ""}`} key={plan.key}>
                <div className="sheet-head">
                  <span className="sheet-plan">{plan.name}</span>
                  <div className="sheet-plate">
                    <strong className="sheet-plate-reg">£{plan.plate}</strong>
                  </div>
                </div>
                <p className="sheet-copy">{plan.copy}</p>
                <ul className="sheet-list">
                  {plan.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                {plan.featured && <p className="sheet-assurance">{completeAssuranceByBrand[brand.key]}</p>}
                <p className="sheet-charge-note">{chargeNoteByKey[plan.key]}</p>
                <a className="sheet-btn" href={`/checkout?plan=${plan.key}`}>
                  {plan.button}
                </a>
              </article>
            ))}
          </div>
          {hasCheapestHpiPage(brand.key) && <p><a href={CHEAPEST_HPI_PATH}>How our HPI check price compares</a>.</p>}

          <h2>What happens if you find no service records?</h2>
          <p>
            {completeAssuranceByBrand[brand.key]} This applies to the £{complete.plate}{" "}
            {complete.name} only. When you order it we authorise the payment but do not take it. We
            then look for the manufacturer&rsquo;s records, and only charge the card once we have
            found some. If there are none, the authorisation is released and nothing leaves your
            account. Depending on your bank the hold can take a few days to disappear from your
            statement.
          </p>
          <p>
            The £{risk.plate} {risk.name} works the normal way and is charged when you order,
            because its checks run against stolen, write-off and finance data we can always reach.
            It is delivered whatever the manufacturer holds, so there is nothing to make it
            conditional on.
          </p>
          <p>{v5cBundleNoteFor(brand.key)}</p>
          <p>
            Manufacturer service records exist for most, but not all, vehicles. Coverage depends on
            the manufacturer, whether the car was serviced at a franchised dealer, and how far back
            that manufacturer&rsquo;s digital records go.
          </p>

          <h2>Optional extras</h2>
          <ul>
            {addOns.map((extra) => (
              <li key={extra.key}>
                <strong>{extra.name}</strong> from £{extra.plate}. {extra.copy}
              </li>
            ))}
          </ul>
          <p>
            V5C logbook monitoring is £1.49 for three months, £2.49 for six months or £3.99 for
            twelve months, added at checkout alongside a vehicle check.
          </p>

          <h2>How do you pay?</h2>
          <p>
            Payment is taken once, by card, through Stripe. {brand.name} never sees or stores your
            card details. There is nothing to cancel afterwards because nothing recurs.
          </p>
          <ul>
            {packages.map((plan) => (
              <li key={plan.key}>
                <strong>
                  {plan.name} £{plan.plate}:
                </strong>{" "}
                {chargeNoteByKey[plan.key]}
              </li>
            ))}
          </ul>

          <div className="plain-note">
            Prices include everything listed. A check covers one registration and the report stays
            available to you afterwards.
          </div>
        </div>
      </section>
    </InfoLayout>
  );
}
